import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { filterNfceCupomFeatures } from '../utils/nfceCupomUi';

export type PublicSaasPlan = {
  id: string;
  name: string;
  price: number | null;
  users_limit: number | null;
  storage_gb: number | null;
  invoices_limit: number | null;
  invoices_overage_price: number | null;
  modules_text: string;
  badge: string | null;
  is_popular: boolean;
  eligible_period_discount: boolean;
  is_enterprise: boolean;
  features: Record<string, boolean>;
};

export type PublicSaasFeature = {
  key: string;
  label: string;
};

export type PrecoPlanCard = {
  id: string;
  name: string;
  price: number;
  users_limit: number | null;
  storage_gb: number | null;
  invoices_limit: number | null;
  invoices_overage_price: number | null;
  users: string;
  storage: string;
  invoicesLimit: string;
  invoicesOverage: string;
  invoicesFiscalEmission: string;
  modulesText: string;
  badge: string | null;
  isPopular: boolean;
  eligiblePeriodDiscount: boolean;
  isEnterprise: boolean;
  features: Record<string, boolean>;
};

const FALLBACK_FEATURES: PublicSaasFeature[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'products_catalog', label: 'Produtos (Catálogo)' },
  { key: 'pdv_sales', label: 'PDV de Vendas' },
  { key: 'customers', label: 'Clientes' },
  { key: 'suppliers', label: 'Fornecedores' },
  { key: 'vehicles', label: 'Veículos' },
  { key: 'repairs', label: 'Oficina O.S.' },
  { key: 'inventory', label: 'Controle de Estoque' },
  { key: 'services', label: 'Serviços' },
  { key: 'purchases_xml', label: 'Compras e XML' },
  { key: 'nfe_product', label: 'Nota de produto (NF-e)' },
  { key: 'nfse_service', label: 'Nota de serviço (NFS-e)' },
  { key: 'nfce_cupom', label: 'Cupom fiscal (NFC-e)' },
  { key: 'financial', label: 'Financeiro' },
  { key: 'employees', label: 'Funcionários' },
  { key: 'user_permissions', label: 'Permissões de Usuários' },
  { key: 'settings', label: 'Configurações' },
  { key: 'support', label: 'Suporte' },
];

const FALLBACK_PLANS: PrecoPlanCard[] = [
  { id: 'v1', name: 'PLANO V1', price: 80, users_limit: 1, storage_gb: 5, invoices_limit: null, invoices_overage_price: null, users: '1 usuário', storage: '5 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Básico', badge: null, isPopular: false, eligiblePeriodDiscount: false, isEnterprise: false, features: {} },
  { id: 'v2', name: 'PLANO V2', price: 199, users_limit: 3, storage_gb: 15, invoices_limit: null, invoices_overage_price: null, users: '3 usuários', storage: '15 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Básico + Financeiro', badge: null, isPopular: false, eligiblePeriodDiscount: false, isEnterprise: false, features: {} },
  { id: 'v3', name: 'PLANO V3', price: 280, users_limit: 5, storage_gb: 30, invoices_limit: null, invoices_overage_price: null, users: '5 usuários', storage: '30 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Comercial', badge: 'Melhor Custo Benefício', isPopular: false, eligiblePeriodDiscount: false, isEnterprise: false, features: {} },
  { id: 'v4', name: 'PLANO V4', price: 380, users_limit: 10, storage_gb: 60, invoices_limit: null, invoices_overage_price: null, users: '10 usuários', storage: '60 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Comercial + Fiscal', badge: 'Mais Vendido', isPopular: true, eligiblePeriodDiscount: true, isEnterprise: false, features: {} },
  { id: 'v5', name: 'PLANO V5', price: 500, users_limit: 20, storage_gb: 120, invoices_limit: null, invoices_overage_price: null, users: '20 usuários', storage: '120 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Completo', badge: null, isPopular: false, eligiblePeriodDiscount: true, isEnterprise: false, features: {} },
  { id: 'v6', name: 'PLANO V6', price: 800, users_limit: -1, storage_gb: 300, invoices_limit: null, invoices_overage_price: null, users: 'Ilimitado', storage: '300 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Completo + Sup.', badge: null, isPopular: false, eligiblePeriodDiscount: true, isEnterprise: false, features: {} },
  { id: 'v7', name: 'PLANO V7', price: 1000, users_limit: -1, storage_gb: 500, invoices_limit: null, invoices_overage_price: null, users: 'Ilimitado', storage: '500 GB', invoicesLimit: '—', invoicesOverage: '—', invoicesFiscalEmission: '—', modulesText: 'Completo + VIP', badge: null, isPopular: false, eligiblePeriodDiscount: true, isEnterprise: false, features: {} },
];

const FALLBACK_ENTERPRISE: PrecoPlanCard = {
  id: 'enterprise',
  name: 'ENTERPRISE',
  price: 0,
  users_limit: null,
  storage_gb: null,
  invoices_limit: null,
  invoices_overage_price: null,
  users: 'Usuários Personalizados',
  storage: 'Armazenamento Personalizado',
  invoicesLimit: 'Sob consulta',
  invoicesOverage: 'Sob consulta',
  invoicesFiscalEmission: 'Sob consulta',
  modulesText: 'Sob demanda',
  badge: 'Premium',
  isPopular: false,
  eligiblePeriodDiscount: false,
  isEnterprise: true,
  features: {},
};

function formatUsers(limit: number | null | undefined): string {
  if (limit == null) return '—';
  if (limit < 0) return 'Ilimitado';
  return limit === 1 ? '1 usuário' : `${limit} usuários`;
}

function formatStorage(gb: number | null | undefined): string {
  if (gb == null) return '—';
  return `${gb} GB`;
}

function formatUsersTable(limit: number | null | undefined): string {
  if (limit == null) return '—';
  if (limit < 0) return 'Ilimitado';
  return String(limit);
}

function formatInvoicesTable(limit: number | null | undefined): string {
  if (limit == null) return '—';
  if (limit < 0) return 'Ilimitado';
  return String(limit);
}

function formatInvoices(limit: number | null | undefined): string {
  if (limit == null) return '—';
  if (limit < 0) return 'Ilimitado';
  return `${limit} notas/mês`;
}

function isUnlimitedInvoiceLimit(limit: number | null | undefined): boolean {
  return limit == null || Number(limit) < 0 || Number(limit) === 0;
}

function formatMoneyBrl(price: number): string {
  return price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Franquia + excedente: preço só após o limite numérico. */
function formatInvoicesOverageTable(
  overagePrice: number | null | undefined,
  invoicesLimit: number | null | undefined,
  isEnterprise?: boolean
): string {
  if (isEnterprise) return 'Sob consulta';
  if (isUnlimitedInvoiceLimit(invoicesLimit)) return '—';
  if (overagePrice == null || overagePrice <= 0) return '—';
  return formatMoneyBrl(Number(overagePrice));
}

/** Pay-per-use: sem franquia + preço > 0 → valor por nota na linha Emissão Fiscal. */
function formatInvoicesFiscalEmissionTable(
  overagePrice: number | null | undefined,
  invoicesLimit: number | null | undefined,
  isEnterprise?: boolean
): string {
  if (isEnterprise) return 'Sob consulta';
  if (!isUnlimitedInvoiceLimit(invoicesLimit)) return '—';
  if (overagePrice == null || overagePrice <= 0) return '—';
  return formatMoneyBrl(Number(overagePrice));
}

function mapApiPlan(p: PublicSaasPlan): PrecoPlanCard {
  return {
    id: p.id,
    name: p.name,
    price: p.price != null ? Number(p.price) : 0,
    users_limit: p.users_limit,
    storage_gb: p.storage_gb,
    invoices_limit: p.invoices_limit,
    invoices_overage_price: p.invoices_overage_price,
    users: p.is_enterprise ? 'Usuários Personalizados' : formatUsers(p.users_limit),
    storage: p.is_enterprise ? 'Armazenamento Personalizado' : formatStorage(p.storage_gb),
    invoicesLimit: p.is_enterprise ? 'Sob consulta' : formatInvoices(p.invoices_limit),
    invoicesOverage: formatInvoicesOverageTable(p.invoices_overage_price, p.invoices_limit, p.is_enterprise),
    invoicesFiscalEmission: formatInvoicesFiscalEmissionTable(
      p.invoices_overage_price,
      p.invoices_limit,
      p.is_enterprise
    ),
    modulesText: p.modules_text || '',
    badge: p.badge,
    isPopular: Boolean(p.is_popular),
    eligiblePeriodDiscount: Boolean(p.eligible_period_discount),
    isEnterprise: Boolean(p.is_enterprise),
    features: p.features || {},
  };
}

function getApiBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  try {
    const { protocol, host } = window.location;
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return `${protocol}//${host.replace(':3000', ':5000')}`;
    }
    // Mesmo domínio da landing (ex.: rostech.com.br/preco → /api/public/...)
    return `${protocol}//${host}/api`;
  } catch {
    return '/api';
  }
}

export function usePublicSaasPlans() {
  const [plans, setPlans] = useState<PrecoPlanCard[]>(FALLBACK_PLANS);
  const [enterprise, setEnterprise] = useState<PrecoPlanCard>(FALLBACK_ENTERPRISE);
  const [features, setFeatures] = useState<PublicSaasFeature[]>(filterNfceCupomFeatures(FALLBACK_FEATURES));
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = getApiBaseUrl();
      const url = `${base}/public/saas/plans`;
      const res = await axios.get<{
        success: boolean;
        data: { plans: PublicSaasPlan[]; features: PublicSaasFeature[] };
      }>(`${url}?_=${Date.now()}`, {
        timeout: 15000,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });

      const apiPlans = res.data?.data?.plans || [];
      const apiFeatures = res.data?.data?.features || FALLBACK_FEATURES;

      const standard = apiPlans.filter((p) => !p.is_enterprise).map(mapApiPlan);
      const ent = apiPlans.find((p) => p.is_enterprise);

      if (standard.length > 0) {
        setPlans(standard);
        setFromApi(true);
      }
      if (ent) {
        setEnterprise(mapApiPlan(ent));
      }
      if (apiFeatures.length > 0) {
        setFeatures(filterNfceCupomFeatures(apiFeatures));
      }
    } catch {
      setPlans(FALLBACK_PLANS);
      setEnterprise(FALLBACK_ENTERPRISE);
      setFeatures(filterNfceCupomFeatures(FALLBACK_FEATURES));
      setFromApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    plans,
    enterprise,
    features,
    loading,
    fromApi,
    reload: load,
    usersLabelForTable: (planId: string) => {
      const p = plans.find((x) => x.id === planId);
      return formatUsersTable(p?.users_limit);
    },
    storageLabelForTable: (planId: string) => {
      const p = plans.find((x) => x.id === planId);
      if (p?.storage_gb == null) return '—';
      return `${p.storage_gb} GB`;
    },
    invoicesLabelForTable: (planId: string) => {
      const p = plans.find((x) => x.id === planId);
      return formatInvoicesTable(p?.invoices_limit);
    },
    invoicesOverageLabelForTable: (planId: string) => {
      const p = plans.find((x) => x.id === planId);
      return formatInvoicesOverageTable(p?.invoices_overage_price, p?.invoices_limit, p?.isEnterprise);
    },
    invoicesFiscalEmissionLabelForTable: (planId: string) => {
      const p = plans.find((x) => x.id === planId);
      return formatInvoicesFiscalEmissionTable(
        p?.invoices_overage_price,
        p?.invoices_limit,
        p?.isEnterprise
      );
    },
    isFeatureEnabled: (planId: string, featureKey: string) => {
      const p = plans.find((x) => x.id === planId);
      if (!p) return false;
      if (p.features && featureKey in p.features) {
        return Boolean(p.features[featureKey]);
      }
      return false;
    },
  };
}
